<template>
  <el-col
    v-if="type === 'dateRange'"
    :xs="16"
    :md="8"
  >
    <el-popover
      class="item"
      effect="dark"
      placement="bottom-start"
      trigger="hover"
      :close-delay="1000"
      >
      <el-select
        v-if="allowNames.length > 0"
        v-model="dateName"
        :placeholder="placeholder"
        @change="dateNameChange"
        >
        <el-option
          v-for="(n, idx) in allowNames"
          :key="idx"
          :label="getOptText(n)"
          :value="n"
          >
        </el-option>
      </el-select>
      <span v-else>
        {{ placeholder }}
      </span>
      <el-date-picker
        slot="reference"
        class="filter-date-range-item"
        v-model="dateRange"
        type="datetimerange"
        range-separator=" ~ "
        @change="dateRangeChange"
        :start-placeholder="$t('api.orders.fields.start_time.label')"
        :end-placeholder="$t('api.orders.fields.end_time.label')"
        :default-time="['00:00:00', '23:59:59']"
        >
      </el-date-picker>
    </el-popover>
  </el-col>
  <el-col
    v-else-if="type === 'select'"
    :xs="16"
    :md="colWidth"
    >
    <el-tooltip
      class="item"
      effect="dark"
      placement="top-start"
      :open-delay="openDelay"
      :content="placeholder"
      >
      <el-select
        v-model="val"
        v-bind="$attrs"
        class="filter-item"
        :placeholder="placeholder"
        @change="selectChange"
        filterable
        clearable
        ref="select"
        >
        <el-option
          v-for="item in list"
          :key="item.id"
          :label="item.name"
          :value="item.id"
          >
        </el-option>
        <template slot="prefix">
          <i class="el-icon-search el-input__icon"></i>
        </template>
      </el-select>
    </el-tooltip>
  </el-col>
  <el-col
    v-else-if="type === 'remoteSelect'"
    :xs="16"
    :md="colWidth"
    >
    <el-tooltip
      class="item"
      effect="dark"
      placement="top-start"
      :open-delay="openDelay"
      :content="placeholder"
      >
      <el-select
        v-model="val"
        v-bind="$attrs"
        class="filter-item"
        :loading="loading"
        :remote-method="getList"
        :value-key="selectIdField"
        @change="selectChange"
        :placeholder="placeholder"
        filterable
        remote
        ref="remoteSelect"
        :popper-class="popperClass"
        >
        <el-option
          v-for="(item, i) in rlist"
          :key="`${i}-${item[selectIdField]}`"
          :label="getLabel(item)"
          :value="item[selectIdField]"
          >
        </el-option>
        <template slot="prefix">
          <i class="el-icon-search el-input__icon"></i>
        </template>
      </el-select>
    </el-tooltip>
  </el-col>
  <el-col
    v-else-if="type === 'checkbox'"
    :xs="8"
    :md="colWidth"
    >
    <el-checkbox
      class="filter-item"
      v-model="val"
      @change="checkboxChange"
      border
      >
      {{ searchText }}
    </el-checkbox>
  </el-col>
  <el-col
    v-else
    :push="$attrs.push"
    :pull="$attrs.pull"
    :span="$attrs.span"
    :xs="16"
    :md="4"
  >
    <el-tooltip
      class="item"
      effect="dark"
      placement="bottom-start"
      :open-delay="openDelay"
      :content="placeholder"
      >
      <el-input
        class="filter-item"
        v-model="val"
        @input="searchInput"
        :placeholder="placeholder"
        prefix-icon="el-icon-search"
        clearable
        >
      </el-input>
    </el-tooltip>
  </el-col>
</template>

<script>

import _ from 'lodash'
import { getDateRange, getDateRangeQuery, getDateTimeFormat, getTimeText } from '@/utils/time'
import { updateRoute } from '@/utils/route'
import { parseField } from '@/utils/filter'
import { getTransText, getLabelText } from '@/utils/i18n'

export default {
  name: 'search-input',
  props: {
    base: {
      type: String,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    searchText: {
      type: String
    },
    param: {
      type: String,
      required: true
    },
    selectStore: {
      type: String,
      default: ''
    },
    selectIdField: {
      type: String,
      default: 'id'
    },
    selectNameField: {
      type: String,
      default: 'name'
    },
    labelName: {
      type: String,
      default: 'name'
    },
    labelArr: {
      type: Array,
      default: () => []
    },
    type: {
      type: String,
      default: 'string' // string, dateRange, select
    },
    allowNames: {
      type: Array,
      default: () => []
    },
    list: {
      type: Array,
      default: () => []
    },
    openDelay: {
      type: Number,
      default: 300
    },
    updListQuery: {
      type: Function
    },
    fetchList: {
      type: Function
    },
    sort: {
      type: String,
      default: 'name'
    },
    pageLimit: {
      type: Number,
      default: 30
    },
    srchFilter: {
      type: String,
      default: '{name:"%`value`%"}'
    },
    defVal: {
      type: String,
      default: ''
    },
    popperClass: {
      type: String,
      default: ''
    },
    colWidth: {
      type: Number,
      default: 4
    }
  },
  data () {
    return {
      val: '',
      loading: false,
      rlist: [],
      rlistObj: {},
      rlistQuery: {
        sort: this.sort,
        page: 1,
        limit: this.pageLimit
      },
      dateName: this.name,
      dateRange: []
    }
  },
  mounted () {
    if (this.type === 'select' && this.selectStore !== '') {
      this.setFromStore()
      this.$ebus.$on('renewLang', this.resetStore)
    } else {
      this.updateVal()
    }
  },
  beforeDestroy () {
    if (this.type === 'select' && this.selectStore !== '') {
      this.$ebus.$off('renewLang', this.resetStore)
    }
  },
  computed: {
    placeholder () {
      switch (this.type) {
        case 'select':
        case 'remoteSelect':
          return this.searchText || this.getTransText(this.name, 'remoteHolder', this.base)
      }
      return this.searchText || this.getLabelText(this.base, this.name)
    }
  },
  watch: {
    '$route.query' () {
      this.updateVal()
    }
  },
  methods: {
    updateRoute,
    parseField,
    getLabelText,
    getTransText,
    getTimeText,
    getDateRange,
    getDateRangeQuery,
    getDateTimeFormat,
    resetStore () {
      this.$store.dispatch('Reset' + this.selectStore)
      this.setFromStore()
    },
    setFromStore () {
      this.$store.dispatch('Init' + this.selectStore).then(() => {
        this.updateVal()
      })
    },
    getLabel (item) {
      if (this.labelArr && this.labelArr.length > 0) {
        var label = ''
        for (var i = 0; i < this.labelArr.length; ++i) {
          if (typeof item[this.labelArr[i]] !== 'undefined' && item[this.labelArr[i]] !== null) {
            label += item[this.labelArr[i]]
            if (i < this.labelArr.length - 1) {
              label += ' - '
            }
          }
        }
        return label
      }
      return item[this.labelName]
    },
    getOptText (n) {
      let arr = this.parseField(this.base, n)
      return this.getLabelText(arr[0], arr[1])
    },
    updateVal () {
      switch (this.type) {
        case 'dateRange':
          this.updDateRangeFilter()
          break
        case 'select':
          this.val = parseInt(this.$route.query[this.param]) || ''
          break
        case 'remoteSelect':
          let id = parseInt(this.$route.query[this.param]) || ''
          let name = this.$route.query[this.name] || ''
          if (id && name) {
            this.rlist = [
              {
                [this.param]: id,
                [this.selectNameField]: name
              }
            ]
          } else {
            this.rlist = []
          }
          this.val = id
          break
        case 'checkbox':
          this.val = (parseInt(this.$route.query[this.param]) === 1)
          break
        default:
          if (this.$route.query[this.param] === undefined && this.defVal !== '') {
            this.updateRoute(this.param, this.defVal)
          }
          this.val = this.$route.query[this.param] || ''
      }
    },
    searchInput: _.debounce(function (query) {
      this.updateRoute(this.param, query)
    }, 500),
    dateRangeChange: _.debounce(function (range) {
      this.updateRoute(this.param, this.getDateRangeQuery(range))
    }, 500),
    dateNameChange (name) {
      this.updateRoute('date_name', name)
    },
    checkboxChange (v) {
      this.updateRoute(
        [{
          'field': this.param,
          'value': (v === true) ? 1 : null
        }])
    },
    selectChange (v) {
      let n = null
      for (let idx in this.list) {
        if (this.list[idx][this.selectIdField] === v) {
          n = this.list[idx][this.selectNameField]
          break
        }
      }
      if (typeof this.rlistObj[v] !== 'undefined') {
        n = this.rlistObj[v][this.selectNameField]
        this.rlist = [
          this.rlistObj[v]
        ]
      }

      this.updateRoute(
        [{
          'field': this.param,
          'value': v
        }, {
          'field': this.name,
          'value': n
        }])
      setTimeout(() => {
        if (typeof this.$refs.remoteSelect !== 'undefined') {
          this.$refs.remoteSelect.blur()
        }
        if (typeof this.$refs.select !== 'undefined') {
          this.$refs.select.blur()
        }
      }, 100)
    },
    getList (query) {
      if (query !== '') {
        let intQ = parseInt(query, 10) || 0
        let f = this.srchFilter.replace(/`value`/g, query)
        f = f.replace(/`int`/g, intQ)
        this.rlistQuery.filter = f
      }
      this.loading = true
      this.fetchList(this.rlistQuery).then(res => {
        let obj = {}
        let arr = []
        for (let i = 0; i < res.data.length; ++i) {
          let d = res.data[i]
          if (typeof obj[d[this.selectIdField]] === 'undefined') {
            obj[d[this.selectIdField]] = d
            arr.push(d)
          }
        }
        this.rlist = arr
        this.rlistObj = obj
      }).catch(error => {
        console.log(error)
      }).then(() => {
        this.loading = false
      })
    },
    updDateRangeFilter () {
      this.dateName = this.$route.query.date_name || this.name
      this.dateRange = this.getDateRange()
    }
  }
}
</script>

<style rel="stylesheet/scss" lang="scss">
  .fixed-select-dropdown {
    max-width: 1px;
    .el-select-dropdown__item {
      overflow: visible;
      display: block;
    }
  }
</style>
